import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Shield } from "lucide-react";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, saveCmsDoc } from "../cmsApi.jsx";
import { defaultLegalDoc } from "../../data/legalDefaults";

function SectionEditor({ title, sections, onChange }) {
  const update = (idx, patch) => {
    const next = [...sections];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () =>
    onChange([
      ...sections,
      { id: `s-${Date.now()}`, title: "Nueva sección", body: "" },
    ]);
  const remove = (idx) => onChange(sections.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">{title}</h3>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-primary"
        >
          <Plus size={14} /> Sección
        </button>
      </div>
      {sections.map((sec, idx) => (
        <div key={sec.id || idx} className="rounded-2xl border border-brand-soft bg-white p-4 space-y-3">
          <div className="flex gap-2">
            <input
              className={inputClass}
              value={sec.title}
              onChange={(e) => update(idx, { title: e.target.value })}
              placeholder="Título"
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-xl"
              aria-label="Eliminar sección"
            >
              <Trash2 size={16} />
            </button>
          </div>
          <textarea
            className={`${inputClass} min-h-[100px]`}
            value={sec.body}
            onChange={(e) => update(idx, { body: e.target.value })}
            placeholder="Contenido de la sección"
          />
        </div>
      ))}
    </div>
  );
}

export default function CmsPrivacidadEditor() {
  const fallback = defaultLegalDoc();
  const { data, loading, source } = useCmsDoc("site", "legal", fallback);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("privacidad");

  useEffect(() => {
    if (!data) return;
    const base = defaultLegalDoc();
    setForm({
      ...base,
      ...structuredClone(data),
      privacySections: data.privacySections?.length ? data.privacySections : base.privacySections,
      cookiesSections: data.cookiesSections?.length ? data.cookiesSections : base.cookiesSections,
      consentMatrix: data.consentMatrix?.length ? data.consentMatrix : base.consentMatrix,
    });
  }, [data]);

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando Privacidad…</p>;
  }

  const onSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const { id, updatedAt, updatedBy, ...payload } = form;
      await saveCmsDoc("site", "legal", payload);
      setMessage("Privacidad y cookies guardados en Firestore.");
    } catch (err) {
      setError(err?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const updateMatrix = (idx, patch) => {
    setForm((prev) => {
      const consentMatrix = [...prev.consentMatrix];
      consentMatrix[idx] = { ...consentMatrix[idx], ...patch };
      return { ...prev, consentMatrix };
    });
  };

  const addMatrix = () =>
    setForm((prev) => ({
      ...prev,
      consentMatrix: [
        ...prev.consentMatrix,
        {
          id: `m-${Date.now()}`,
          label: "Nuevo ítem",
          withConsent: "",
          withoutConsent: "",
        },
      ],
    }));

  const removeMatrix = (idx) =>
    setForm((prev) => ({
      ...prev,
      consentMatrix: prev.consentMatrix.filter((_, i) => i !== idx),
    }));

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand-primary flex items-center justify-center">
              <Shield size={20} />
            </div>
            <div>
              <h2 className="font-display text-3xl font-black">Privacidad y cookies</h2>
              <p className="text-sm text-brand-dark/55 mt-1">
                Textos públicos en{" "}
                <Link to="/privacidad" className="text-brand-primary font-semibold underline" target="_blank">
                  /privacidad
                </Link>{" "}
                y{" "}
                <Link to="/cookies" className="text-brand-primary font-semibold underline" target="_blank">
                  /cookies
                </Link>
                . Fuente: {source === "firestore" ? "Firestore" : "valores por defecto"}.
              </p>
            </div>
          </div>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-brand-soft">
        {[
          ["privacidad", "Aviso de privacidad"],
          ["cookies", "Política de cookies"],
          ["matriz", "Con / sin consentimiento"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm ${
              tab === id
                ? "border-b-2 border-brand-primary font-bold text-brand-primary"
                : "text-brand-dark/55 hover:text-brand-dark"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "privacidad" && (
        <div className="space-y-4">
          <Field label="Título">
            <input
              className={inputClass}
              value={form.privacyTitle}
              onChange={(e) => setForm((p) => ({ ...p, privacyTitle: e.target.value }))}
            />
          </Field>
          <Field label="Subtítulo">
            <input
              className={inputClass}
              value={form.privacySubtitle}
              onChange={(e) => setForm((p) => ({ ...p, privacySubtitle: e.target.value }))}
            />
          </Field>
          <Field label="Fecha de actualización (texto)">
            <input
              className={inputClass}
              value={form.privacyUpdated}
              onChange={(e) => setForm((p) => ({ ...p, privacyUpdated: e.target.value }))}
            />
          </Field>
          <Field label="Correo de contacto ARCO-POL">
            <input
              className={inputClass}
              value={form.privacyContact}
              onChange={(e) => setForm((p) => ({ ...p, privacyContact: e.target.value }))}
            />
          </Field>
          <SectionEditor
            title="Secciones del aviso"
            sections={form.privacySections}
            onChange={(privacySections) => setForm((p) => ({ ...p, privacySections }))}
          />
        </div>
      )}

      {tab === "cookies" && (
        <div className="space-y-4">
          <Field label="Título">
            <input
              className={inputClass}
              value={form.cookiesTitle}
              onChange={(e) => setForm((p) => ({ ...p, cookiesTitle: e.target.value }))}
            />
          </Field>
          <Field label="Subtítulo">
            <input
              className={inputClass}
              value={form.cookiesSubtitle}
              onChange={(e) => setForm((p) => ({ ...p, cookiesSubtitle: e.target.value }))}
            />
          </Field>
          <Field label="Fecha de actualización">
            <input
              className={inputClass}
              value={form.cookiesUpdated}
              onChange={(e) => setForm((p) => ({ ...p, cookiesUpdated: e.target.value }))}
            />
          </Field>
          <Field label="Introducción">
            <textarea
              className={`${inputClass} min-h-[90px]`}
              value={form.cookiesIntro}
              onChange={(e) => setForm((p) => ({ ...p, cookiesIntro: e.target.value }))}
            />
          </Field>
          <SectionEditor
            title="Secciones adicionales"
            sections={form.cookiesSections}
            onChange={(cookiesSections) => setForm((p) => ({ ...p, cookiesSections }))}
          />
        </div>
      )}

      {tab === "matriz" && (
        <div className="space-y-4">
          <p className="text-sm text-brand-dark/60">
            Por cada ítem indique qué ocurre <strong>con consentimiento</strong> y{" "}
            <strong>sin consentimiento</strong>. Se muestra en /cookies.
          </p>
          <button
            type="button"
            onClick={addMatrix}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-primary"
          >
            <Plus size={14} /> Ítem
          </button>
          {form.consentMatrix.map((row, idx) => (
            <div key={row.id || idx} className="rounded-2xl border border-brand-soft bg-white p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  className={inputClass}
                  value={row.label}
                  onChange={(e) => updateMatrix(idx, { label: e.target.value })}
                  placeholder="Nombre del ítem"
                />
                <button
                  type="button"
                  onClick={() => removeMatrix(idx)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-xl"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <Field label="Con consentimiento">
                <textarea
                  className={`${inputClass} min-h-[72px]`}
                  value={row.withConsent}
                  onChange={(e) => updateMatrix(idx, { withConsent: e.target.value })}
                />
              </Field>
              <Field label="Sin consentimiento">
                <textarea
                  className={`${inputClass} min-h-[72px]`}
                  value={row.withoutConsent}
                  onChange={(e) => updateMatrix(idx, { withoutConsent: e.target.value })}
                />
              </Field>
            </div>
          ))}
        </div>
      )}

      <Field label="Nota al pie (disclaimer)">
        <textarea
          className={`${inputClass} min-h-[60px]`}
          value={form.legalDisclaimer}
          onChange={(e) => setForm((p) => ({ ...p, legalDisclaimer: e.target.value }))}
        />
      </Field>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-brand-primary font-medium">{message}</p>}
      <SaveBar saving={saving} onSave={onSave} />
    </div>
  );
}
